import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getLabExternalStorageProviders,
  labExternalStorageProvidersQueryKey,
  validateLabExternalStorageProvider,
} from 'api/externalStorageProviders';
import {
  LabExternalStorageProvider,
  StudyExternalStorageBucketSetting,
  StudyExternalStorageSettings,
} from 'interfaces/externalStorage';
import { Study } from 'interfaces/study';
import { every, filter, find, includes, map } from 'lodash';
import React, { useState } from 'react';
import { getQueryErrorMessage } from 'utils/httpError';

type BucketSettingsKey = keyof StudyExternalStorageSettings;

interface SectionDefinition {
  key: BucketSettingsKey;
  title: string;
  description: string;
  addLabel: string;
  emptyMessage: string;
  permissionMessage: string;
}

interface ValidationState {
  key: string;
  severity: 'error' | 'success';
  message: string;
}

const EMPTY_EXTERNAL_STORAGE_SETTINGS: StudyExternalStorageSettings = {
  slideSourceBucketSettings: [],
  resultExportBucketSettings: [],
};

const createEmptyBucketSetting = (): StudyExternalStorageBucketSetting => ({
  providerName: '',
  bucket: '',
  prefix: '',
  displayName: '',
  enabled: true,
});

const isBucketSettingComplete = (setting: StudyExternalStorageBucketSetting) =>
  Boolean(setting.providerName.trim() && setting.bucket.trim());

export const areExternalStorageSettingsComplete = (settings?: Study['settings']) => {
  const externalStorage = settings?.externalStorage;

  if (!externalStorage) {
    return true;
  }

  return every(
    [...externalStorage.slideSourceBucketSettings, ...externalStorage.resultExportBucketSettings],
    isBucketSettingComplete
  );
};

const sectionDefinitions: SectionDefinition[] = [
  {
    key: 'slideSourceBucketSettings',
    title: 'External slide source buckets',
    description: 'Approved locations that users can browse when transferring slides into this study.',
    addLabel: 'Add slide source bucket',
    emptyMessage: 'No external slide source buckets are configured for this study.',
    permissionMessage: 'You don’t have permission to configure slide source buckets.',
  },
  {
    key: 'resultExportBucketSettings',
    title: 'Result export buckets',
    description: 'Locations that receive published result artifacts for this study.',
    addLabel: 'Add result export bucket',
    emptyMessage: 'No result export bucket is configured. Result publishing requires a provider-backed export bucket.',
    permissionMessage: 'You don’t have permission to configure result export buckets.',
  },
];

export interface StudyExternalStorageSettingsProps {
  study: Study;
  updatedSettings: Study['settings'];
  setUpdatedSettings: React.Dispatch<React.SetStateAction<Study['settings']>>;
  canEditSlideSourceBuckets: boolean;
  canEditResultExportBuckets: boolean;
}

export const StudyExternalStorageSettingsControl: React.FC<StudyExternalStorageSettingsProps> = ({
  study,
  updatedSettings,
  setUpdatedSettings,
  canEditSlideSourceBuckets,
  canEditResultExportBuckets,
}) => {
  const [touchedFields, setTouchedFields] = useState<string[]>([]);
  const [validationState, setValidationState] = useState<ValidationState | null>(null);

  const providersQuery = useQuery({
    queryKey: labExternalStorageProvidersQueryKey(study.labId),
    queryFn: ({ signal }) => getLabExternalStorageProviders(study.labId, signal),
    enabled: Boolean(study.labId),
  });

  const validationMutation = useMutation(
    ({ providerName, bucket, prefix }: { providerName: string; bucket: string; prefix?: string }) =>
      validateLabExternalStorageProvider(study.labId, providerName, { bucket, prefix }),
    {
      onSuccess: (result, variables) =>
        setValidationState({
          key: variables.providerName + ':' + variables.bucket + ':' + (variables.prefix || ''),
          severity: result.validationStatus === 'validated' ? 'success' : 'error',
          message:
            result.message ||
            `Access to ${result.bucket}${result.prefix ? `/${result.prefix}` : ''} was validated successfully.${
              result.acceleratedEndpointEnabled === false
                ? ' S3 Transfer Acceleration is not enabled for this bucket.'
                : ''
            }`,
        }),
      onError: (error, variables) =>
        setValidationState({
          key: variables.providerName + ':' + variables.bucket + ':' + (variables.prefix || ''),
          severity: 'error',
          message: getQueryErrorMessage(error, 'Bucket access could not be validated.'),
        }),
    }
  );

  const externalStorage = updatedSettings?.externalStorage || EMPTY_EXTERNAL_STORAGE_SETTINGS;
  const enabledProviders = filter(providersQuery.data || [], 'enabled');

  const updateBucketSettings = (key: BucketSettingsKey, bucketSettings: StudyExternalStorageBucketSetting[]) => {
    setValidationState(null);
    setUpdatedSettings((previousSettings) => ({
      ...previousSettings,
      externalStorage: {
        slideSourceBucketSettings: previousSettings?.externalStorage?.slideSourceBucketSettings || [],
        resultExportBucketSettings: previousSettings?.externalStorage?.resultExportBucketSettings || [],
        [key]: bucketSettings,
      },
    }));
  };

  const updateBucketSetting = (
    key: BucketSettingsKey,
    index: number,
    update: Partial<StudyExternalStorageBucketSetting>
  ) => {
    updateBucketSettings(
      key,
      map(externalStorage[key], (setting, settingIndex) =>
        settingIndex === index ? { ...setting, ...update } : setting
      )
    );
  };

  const getValidationKey = (setting: StudyExternalStorageBucketSetting) =>
    setting.providerName + ':' + setting.bucket + ':' + (setting.prefix || '');

  const renderBucketSetting = (
    definition: SectionDefinition,
    setting: StudyExternalStorageBucketSetting,
    index: number,
    canEdit: boolean
  ) => {
    const fieldKey = `${definition.key}-${index}`;
    const providerTouched = includes(touchedFields, `${fieldKey}-provider`);
    const bucketTouched = includes(touchedFields, `${fieldKey}-bucket`);
    const selectedProvider = find(providersQuery.data, { name: setting.providerName });
    const currentValidationKey = getValidationKey(setting);
    const isValidating =
      validationMutation.isLoading && validationMutation.variables
        ? getValidationKey(validationMutation.variables as StudyExternalStorageBucketSetting) === currentValidationKey
        : false;

    return (
      <Paper key={fieldKey} variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Typography variant="subtitle2">Bucket setting {index + 1}</Typography>
            {canEdit && (
              <Tooltip title="Remove bucket setting">
                <IconButton
                  aria-label={`Remove ${definition.title.toLowerCase()} setting ${index + 1}`}
                  onClick={() =>
                    updateBucketSettings(
                      definition.key,
                      filter(externalStorage[definition.key], (_, itemIndex) => itemIndex !== index)
                    )
                  }
                  size="small"
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                required
                label="Provider"
                value={setting.providerName}
                disabled={!canEdit || providersQuery.isLoading}
                error={providerTouched && !setting.providerName}
                helperText={
                  providerTouched && !setting.providerName
                    ? 'Select an enabled lab provider.'
                    : 'Credentials remain managed in Lab settings.'
                }
                onBlur={() => setTouchedFields((fields) => [...fields, `${fieldKey}-provider`])}
                onChange={(event) => updateBucketSetting(definition.key, index, { providerName: event.target.value })}
              >
                {setting.providerName && !selectedProvider?.enabled && (
                  <MenuItem value={setting.providerName} disabled>
                    {setting.providerName} (unavailable)
                  </MenuItem>
                )}
                {map(enabledProviders, (provider: LabExternalStorageProvider) => (
                  <MenuItem key={provider.name} value={provider.name}>
                    {provider.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Bucket"
                value={setting.bucket}
                disabled={!canEdit}
                error={bucketTouched && !setting.bucket.trim()}
                helperText={
                  bucketTouched && !setting.bucket.trim() ? 'Enter a bucket name.' : 'Bucket name without s3://.'
                }
                onBlur={() => setTouchedFields((fields) => [...fields, `${fieldKey}-bucket`])}
                onChange={(event) => updateBucketSetting(definition.key, index, { bucket: event.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Root prefix (optional)"
                value={setting.prefix || ''}
                disabled={!canEdit}
                helperText="Limit this setting to a folder within the bucket."
                onChange={(event) => updateBucketSetting(definition.key, index, { prefix: event.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Display name (optional)"
                value={setting.displayName || ''}
                disabled={!canEdit}
                helperText="A recognizable label for users choosing this location."
                onChange={(event) => updateBucketSetting(definition.key, index, { displayName: event.target.value })}
              />
            </Grid>
          </Grid>

          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={setting.enabled}
                  disabled={!canEdit}
                  onChange={(event) => updateBucketSetting(definition.key, index, { enabled: event.target.checked })}
                />
              }
              label="Enabled"
            />
            <Button
              variant="outlined"
              disabled={!canEdit || !isBucketSettingComplete(setting) || isValidating}
              onClick={() =>
                validationMutation.mutate({
                  providerName: setting.providerName,
                  bucket: setting.bucket.trim(),
                  prefix: setting.prefix?.trim() || undefined,
                })
              }
              startIcon={isValidating ? <CircularProgress size={16} /> : undefined}
            >
              {isValidating ? 'Validating...' : 'Validate access'}
            </Button>
          </Stack>

          {validationState?.key === currentValidationKey && (
            <Alert severity={validationState.severity}>{validationState.message}</Alert>
          )}
        </Stack>
      </Paper>
    );
  };

  if (providersQuery.isError) {
    return (
      <Alert severity="error">{getQueryErrorMessage(providersQuery.error, 'Lab providers could not be loaded.')}</Alert>
    );
  }

  return (
    <Stack spacing={4}>
      {providersQuery.isLoading && <Alert severity="info">Loading lab external storage providers...</Alert>}
      {!providersQuery.isLoading && !enabledProviders.length && (
        <Alert severity="warning">
          No enabled external storage providers are available. Configure one in Lab settings before adding a bucket.
        </Alert>
      )}

      <Grid container spacing={2} alignItems="stretch">
        {map(sectionDefinitions, (definition) => {
          const bucketSettings = externalStorage[definition.key];
          const canEdit =
            definition.key === 'slideSourceBucketSettings' ? canEditSlideSourceBuckets : canEditResultExportBuckets;

          return (
            <Grid item xs={12} lg={6} key={definition.key} sx={{ display: 'flex', minWidth: 0 }}>
              <Paper
                component="section"
                variant="outlined"
                aria-labelledby={`${definition.key}-title`}
                sx={{ p: 2, width: '100%' }}
              >
                <Stack spacing={2} sx={{ height: '100%' }}>
                  <Box>
                    <Typography id={`${definition.key}-title`} variant="h6">
                      {definition.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {definition.description}
                    </Typography>
                    {!canEdit && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {definition.permissionMessage}
                      </Typography>
                    )}
                  </Box>

                  {!bucketSettings.length && enabledProviders.length ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      {definition.emptyMessage}
                    </Typography>
                  ) : (
                    map(bucketSettings, (setting, index) => renderBucketSetting(definition, setting, index, canEdit))
                  )}

                  <Box sx={{ mt: 'auto' }}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      disabled={!canEdit || providersQuery.isLoading || !enabledProviders.length}
                      onClick={() =>
                        updateBucketSettings(definition.key, [...bucketSettings, createEmptyBucketSetting()])
                      }
                    >
                      {definition.addLabel}
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Stack>
  );
};
